<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import api from '@/api'
import Swal from 'sweetalert2'
import { exportPagosSocioExcel } from '@/utils/exportExcel'
import { hasPermission } from '@/composables/useAuth'

const canRegistrarPago = computed(() => hasPermission('pagos.registrar'))
const canAnularPago = computed(() => hasPermission('pagos.anular'))

const props = defineProps({ usuarioId: { type: [String, Number], required: true } })
const cargando = ref(false)
const guardando = ref(false)
const anulandoId = ref(null)
const instancias = ref([])
const seleccion = ref(null)
const pagos = ref([])
const page = ref(1)
// eslint-disable-next-line no-unused-vars
const limit = ref(10)
const total = ref(0)
const hoy = new Date()
const startDate = ref(new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10))
const endDate = ref(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().slice(0, 10))

// Importes devueltos por el backend; el cliente nunca calcula el saldo.
const resumen = ref({ precio_lista: '0.00', descuento_porcentaje: '0.00', descuento_monto: '0.00', precio_unitario: '0.00', total_abonado: '0.00', saldo: '0.00' })
const form = ref({ fecha_pago: new Date().toISOString().slice(0, 10), monto_recibido: '', metodo_pago: '', observaciones: '' })

const instanciaSeleccionada = computed(() => instancias.value.find((item) => item.id === seleccion.value) || null)
const saldo = computed(() => resumen.value.saldo ?? resumen.value.saldo_pendiente ?? '0.00')
const pagosBloqueados = computed(() => Number(saldo.value) <= 0)
const puedePrev = computed(() => false)
const puedeNext = computed(() => false)

function fmtMoney(value) {
  return Number(value ?? 0).toLocaleString('es-EC', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
}
function fmtDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toISOString().slice(0, 10)
}
function normalizarResumen(data = {}) {
  const instancia = instanciaSeleccionada.value || {}
  return {
    precio_lista: data.precio_lista ?? instancia.precio_lista ?? '0.00',
    descuento_porcentaje: data.descuento_porcentaje ?? instancia.descuento_porcentaje ?? '0.00',
    descuento_monto: data.descuento_monto ?? instancia.descuento_monto ?? '0.00',
    precio_unitario: data.precio_unitario ?? instancia.precio_unitario ?? '0.00',
    total_abonado: data.total_abonado ?? data.pagado_total ?? instancia.total_abonado ?? instancia.pagado_total ?? '0.00',
    saldo: data.saldo ?? data.saldo_pendiente ?? instancia.saldo ?? instancia.saldo_pendiente ?? '0.00'
  }
}

async function cargarInstancias() {
  try {
    cargando.value = true
    const { data } = await api.get(`/membresia-usuarios/usuario/${props.usuarioId}`)
    const ordenadas = [...(data || [])].sort((a, b) => new Date(b.fecha_inicio) - new Date(a.fecha_inicio))
    const activa = ordenadas.find((item) => item.estado === 'activa')
    instancias.value = (activa ? [activa, ...ordenadas.filter((item) => item.id !== activa.id)] : ordenadas).slice(0, 10)
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.error(error)
    Swal.fire('Error', 'No se pudieron cargar las membresías del socio', 'error')
  } finally { cargando.value = false }
}
async function cargarResumen() {
  if (!seleccion.value) return
  try {
    const { data } = await api.get(`/pagos-membresia/membresia-usuarios/${seleccion.value}/saldo`)
    resumen.value = normalizarResumen(data)
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.error(error)
    Swal.fire('Error', 'No se pudo obtener la información financiera de la membresía', 'error')
  }
}
async function cargarPagos() {
  if (!seleccion.value) return
  try {
    // FE-028: usar endpoint filtrado por membresia_usuario_id en backend — no filtrar página genérica
    const { data } = await api.get(`/pagos-membresia/membresia-usuarios/${seleccion.value}/pagos`)
    const rows = data?.pagos || (Array.isArray(data) ? data : [])
    // Filtro opcional por rango fecha en cliente (endpoint no soporta paginación server)
    let filtered = Array.isArray(rows) ? rows : []
    if (startDate.value && endDate.value) {
      filtered = filtered.filter((p) => {
        const f = (p.fecha_pago || '').slice(0, 10)
        return f >= startDate.value && f <= endDate.value
      })
    }
    pagos.value = filtered
    total.value = pagos.value.length
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.error(error)
    Swal.fire('Error', 'No se pudieron cargar los pagos', 'error')
  }
}

async function registrarPago() {
  if (!instanciaSeleccionada.value) return Swal.fire('Atención', 'Seleccione una membresía del socio', 'warning')
  if (!form.value.monto_recibido || Number(form.value.monto_recibido) <= 0) return Swal.fire('Atención', 'Ingrese un monto recibido mayor que cero', 'warning')
  if (!form.value.metodo_pago) return Swal.fire('Atención', 'Seleccione el método de pago', 'warning')
  if (pagosBloqueados.value) return Swal.fire('Saldo cancelado', 'Esta membresía no tiene saldo pendiente.', 'info')

  guardando.value = true
  try {
    const { data } = await api.post('/pagos-membresia', {
      membresia_usuario_id: instanciaSeleccionada.value.id,
      monto_recibido: form.value.monto_recibido,
      metodo_pago: form.value.metodo_pago,
      fecha_pago: form.value.fecha_pago || undefined,
      observaciones: form.value.observaciones || undefined
    })
    await cargarResumen()
    await cargarPagos()
    form.value.monto_recibido = ''
    form.value.metodo_pago = ''
    form.value.observaciones = ''
    const pago = data?.pago || data
    const cambio = pago?.cambio ?? data?.cambio ?? 0
    await Swal.fire({ icon: 'success', title: 'Pago registrado', text: Number(cambio) > 0 ? `Cambio: ${fmtMoney(cambio)}` : 'El abono fue registrado correctamente.' })
  } catch (error) {
    const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Error al registrar pago'
    await Swal.fire('Pago no registrado', message, 'error')
    if (process.env.NODE_ENV !== 'production') console.error(error)
  } finally { guardando.value = false }
}

async function anularPago(pago) {
  const { value: motivo } = await Swal.fire({
    title: 'Anular pago', input: 'textarea', inputLabel: 'Motivo de anulación', inputPlaceholder: 'Describa el motivo',
    inputValidator: (value) => !value?.trim() && 'El motivo de anulación es obligatorio.',
    showCancelButton: true, confirmButtonText: 'Anular pago', cancelButtonText: 'Cancelar', preConfirm: (value) => value.trim()
  })
  if (!motivo) return
  anulandoId.value = pago.id
  try {
    await api.patch(`/pagos-membresia/${pago.id}/anular`, { motivo_anulacion: motivo })
    await cargarResumen(); await cargarPagos()
    await Swal.fire('Pago anulado', 'El pago continúa visible en el historial.', 'success')
  } catch (error) {
    await Swal.fire('Error', error.response?.data?.message || error.response?.data?.error || 'No se pudo anular el pago', 'error')
  } finally { anulandoId.value = null }
}
function prevPage() { if (puedePrev.value) { page.value -= 1; cargarPagos() } }
function nextPage() { if (puedeNext.value) { page.value += 1; cargarPagos() } }
async function exportarExcelSocio() {
  if (!pagos.value.length) return Swal.fire('Sin datos', 'No hay pagos para exportar', 'info')
  try { await exportPagosSocioExcel(pagos.value, { socioNombre: instanciaSeleccionada.value?.usuario?.nombre || `socio_${props.usuarioId}` }) }
  catch (error) { if (process.env.NODE_ENV !== 'production') console.error(error); Swal.fire('Error', 'No se pudo exportar', 'error') }
}

onMounted(async () => {
  await cargarInstancias()
  const activa = instancias.value.find((item) => item.estado === 'activa')
  seleccion.value = activa?.id || instancias.value[0]?.id || null
})
watch(seleccion, async (value) => {
  page.value = 1
  if (value) { await cargarResumen(); await cargarPagos() }
  else { resumen.value = normalizarResumen(); pagos.value = []; total.value = 0 }
})
</script>

<template>
  <div class="py-3">
    <div class="card p-3 mb-3"><div class="row g-3 align-items-end">
      <div class="col-md-4"><label class="form-label">Membresía del socio</label><select v-model="seleccion" class="form-select"><option :value="null" disabled>— Seleccione —</option><option v-for="m in instancias" :key="m.id" :value="m.id">{{ m.id }} — {{ m.membresia?.nombre || 'Membresía' }} · {{ fmtDate(m.fecha_inicio) }} → {{ fmtDate(m.fecha_fin) }} · {{ m.estado?.toUpperCase() }}</option></select></div>
      <div class="col-md-8"><div class="d-flex flex-wrap gap-3"><div><small class="text-muted">Precio lista: </small><strong>{{ fmtMoney(resumen.precio_lista) }}</strong></div><div><small class="text-muted">Descuento: </small><strong>{{ resumen.descuento_porcentaje }}% ({{ fmtMoney(resumen.descuento_monto) }})</strong></div><div><small class="text-muted">Precio final: </small><strong>{{ fmtMoney(resumen.precio_unitario) }}</strong></div><div><small class="text-muted">Total abonado: </small><strong>{{ fmtMoney(resumen.total_abonado) }}</strong></div><div><small class="text-muted">Saldo: </small><strong :class="{ 'text-success': Number(saldo) === 0, 'text-danger': Number(saldo) > 0 }">{{ fmtMoney(saldo) }}</strong></div></div></div>
    </div></div>
    <div v-if="canRegistrarPago" class="card p-3 mb-3"><div v-if="pagosBloqueados" class="alert alert-success mb-3">Esta membresía está completamente saldada. No se permiten más pagos.</div><div class="row g-3 align-items-end">
      <div class="col-md-3"><label class="form-label">Fecha pago</label><input v-model="form.fecha_pago" type="date" class="form-control" :disabled="pagosBloqueados || guardando || !seleccion"></div><div class="col-md-3"><label class="form-label">Monto recibido</label><input v-model="form.monto_recibido" type="number" step="0.01" min="0.01" class="form-control" :disabled="pagosBloqueados || guardando || !seleccion"></div><div class="col-md-3"><label class="form-label">Método</label><select v-model="form.metodo_pago" class="form-select" :disabled="pagosBloqueados || guardando || !seleccion"><option value="">— Seleccione —</option><option value="efectivo">Efectivo</option><option value="transferencia">Transferencia</option><option value="tarjeta">Tarjeta</option></select></div><div class="col-md-3"><button class="btn btn-primary w-100" :disabled="pagosBloqueados || guardando || !seleccion" @click="registrarPago">Guardar pago</button></div><div class="col-md-12"><label class="form-label">Observaciones</label><input v-model="form.observaciones" type="text" class="form-control" placeholder="Opcional" :disabled="pagosBloqueados || guardando || !seleccion"></div>
    </div></div>
    <div v-else class="alert alert-info small">No tienes permiso para registrar pagos (requiere pagos.registrar).</div>
    <div class="card p-3"><div class="row g-3 align-items-end mb-3"><div class="col-md-3 col-6"><label class="form-label">Desde</label><input v-model="startDate" type="date" class="form-control"></div><div class="col-md-3 col-6"><label class="form-label">Hasta</label><input v-model="endDate" type="date" class="form-control"></div><div class="col-md-2 col-12"><button class="btn btn-outline-secondary w-100 mb-0" @click="() => { page = 1; cargarPagos() }">Filtrar</button></div><div class="col-md-2 col-12"><button class="btn btn-outline-success w-100 mb-0" @click="exportarExcelSocio" :disabled="!pagos.length"><i class="fa fa-file-excel me-1"></i> Excel</button></div></div>
      <div class="table-responsive"><table class="table table-sm align-middle"><thead class="table-light"><tr><th>Fecha</th><th>Estado</th><th>Método</th><th>Recibido</th><th>Aplicado</th><th>Cambio</th><th>Obs.</th><th></th></tr></thead><tbody><tr v-for="p in pagos" :key="p.id" :class="{ 'table-secondary text-muted': p.estado === 'anulado' }"><td>{{ fmtDate(p.fecha_pago) }}</td><td><span class="badge" :class="p.estado === 'anulado' ? 'bg-secondary' : 'bg-success'">{{ (p.estado || 'confirmado').toUpperCase() }}</span></td><td>{{ p.metodo_pago }}</td><td>{{ fmtMoney(p.monto_recibido ?? p.monto_pagado) }}</td><td>{{ fmtMoney(p.monto_pagado) }}</td><td>{{ fmtMoney(p.cambio) }}</td><td>{{ p.estado === 'anulado' ? (p.motivo_anulacion || 'Pago anulado') : (p.observaciones || '—') }}</td><td><button v-if="p.estado !== 'anulado' && canAnularPago" class="btn btn-outline-danger btn-sm" :disabled="anulandoId === p.id" @click="anularPago(p)">Anular</button></td></tr><tr v-if="!pagos.length"><td colspan="8" class="text-center text-muted">Sin pagos registrados</td></tr></tbody></table></div>
      <div class="d-flex justify-content-between align-items-center"><small class="text-muted">Página {{ page }} · {{ total }} pagos</small><div class="d-flex gap-2"><button class="btn btn-outline-secondary btn-sm" :disabled="!puedePrev" @click="prevPage">Anterior</button><button class="btn btn-outline-secondary btn-sm" :disabled="!puedeNext" @click="nextPage">Siguiente</button></div></div>
    </div>
  </div>
</template>
<style scoped>.table td, .table th { font-size: 0.85rem; }</style>
